import { ArrowLeft, Mail, Phone } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useState, useEffect, useRef } from "react";
import { StepProgress } from "@/components/booking/StepProgress";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

// Canadian NPAs (Area Codes) - Complete comprehensive list
// All known Canadian area codes to ensure US numbers are properly identified
// Any +1 number not in this list will be treated as US (maximum coverage)
const CANADIAN_NPAS = new Set([
  // Alberta: 368, 403, 587, 780, 825
  '368', '403', '587', '780', '825',
  // British Columbia: 236, 250, 604, 672, 778
  '236', '250', '604', '672', '778',
  // Manitoba: 204, 431
  '204', '431',
  // New Brunswick: 506
  '506',
  // Newfoundland and Labrador: 709
  '709',
  // Nova Scotia & Prince Edward Island: 782, 902
  '782', '902',
  // Ontario: 226, 236, 249, 289, 343, 365, 416, 437, 438, 519, 548, 613, 647, 705, 807, 905
  '226', '249', '289', '343', '365', '416', '437', '519', '548', '613', '647', '705', '807', '905',
  // Quebec: 367, 418, 438, 450, 514, 579, 581, 819, 873
  '367', '418', '450', '514', '579', '581', '819', '873',
  // Saskatchewan: 306, 639
  '306', '639',
  // Yukon, Northwest Territories & Nunavut: 867
  '867',
  // Additional overlay codes (shared across provinces): 742, 753
  '742', '753'
]);

// Toll-free and non-geographic NPAs (shared between US and Canada)
const TOLL_FREE_NPAS = new Set(['800', '833', '844', '855', '866', '877', '888']);
const NON_GEO_5XX = new Set(['500', '521', '522', '523', '524', '525', '526', '527', '528', '529']);

/**
 * Detects phone country based on NPA (Numbering Plan Area / Area Code)
 * For +1 numbers, differentiates between US and Canada
 * @param phonePrefix - Country code prefix (e.g., "+1", "+55")
 * @param phoneNumber - Phone number without prefix (e.g., "4161234567" or "416 123-4567")
 * @returns 'US' | 'CA' | 'BR' | 'OTHER'
 */
function detectPhoneCountry(phonePrefix: string, phoneNumber: string): 'US' | 'CA' | 'BR' | 'OTHER' {
  // Brazil (+55) for testing
  if (phonePrefix === '+55') {
    return 'BR';
  }

  // Only process +1 numbers (NANP)
  if (phonePrefix !== '+1') {
    return 'OTHER';
  }

  // Normalize: remove all non-digit characters
  let digits = phoneNumber.replace(/\D/g, '');

  // If starts with 1 and has 11 digits, remove the leading 1
  if (digits.startsWith('1') && digits.length === 11) {
    digits = digits.slice(1);
  }

  // Validate NANP: must be exactly 10 digits
  if (digits.length !== 10) {
    return 'OTHER';
  }

  // Extract NPA (first 3 digits) and NXX (next 3 digits)
  const npa = digits.slice(0, 3);
  const nxx = digits.slice(3, 6);

  // Validate NPA and NXX: must start with 2-9 (NANP rule)
  const npaFirstDigit = parseInt(npa[0]);
  const nxxFirstDigit = parseInt(nxx[0]);
  
  if (npaFirstDigit < 2 || npaFirstDigit > 9 || nxxFirstDigit < 2 || nxxFirstDigit > 9) {
    return 'OTHER';
  }

  // Classify by NPA
  // If it's a Canadian NPA, return Canada
  if (CANADIAN_NPAS.has(npa)) {
    return 'CA';
  }
  
  // Toll-free and non-geographic numbers are shared between US and Canada
  // Default to US for these as they're more commonly used in the US
  if (TOLL_FREE_NPAS.has(npa) || NON_GEO_5XX.has(npa)) {
    return 'US';
  }

  // Maximum US coverage: Any valid NANP NPA that is not Canadian is treated as US
  // This covers ALL US area codes (201-989) except:
  // - Canadian codes (already excluded above)
  // - Toll-free codes (800, 833, 844, 855, 866, 877, 888) - treated as US
  // - Non-geographic codes (500-529) - treated as US
  // - Invalid NPA ranges (211, 311, 411, 511, 611, 711, 811, 911) - filtered by NXX validation
  
  // This approach maximizes US number detection:
  // - All US state area codes (201-989) are covered
  // - All US territories (Puerto Rico, US Virgin Islands, etc.) are covered
  // - Any new US area codes will automatically be detected as US
  // - Only Canadian codes are excluded, ensuring maximum US coverage
  
  return 'US';
}

export default function BookVerify() {
  const navigate = useNavigate();
  const { sendSupabaseOTP, verifySupabaseOTP, signUpWithEmailPassword, signInWithEmailPassword } = useAuth();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(30);
  const [consent, setConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [phoneCountry, setPhoneCountry] = useState<'US' | 'CA' | 'BR' | 'OTHER'>('OTHER');
  const [shouldPrioritizeEmail, setShouldPrioritizeEmail] = useState(false);
  const [smsSent, setSmsSent] = useState(false); // Track if SMS was sent in email priority mode
  const hasSentSMS = useRef(false);

  // Detect phone country and determine if email should be prioritized
  useEffect(() => {
    const formDataStr = sessionStorage.getItem('bookingFormData');
    if (formDataStr) {
      try {
        const formData = JSON.parse(formDataStr);
        const country = detectPhoneCountry(formData.phonePrefix, formData.phone || '');
        setPhoneCountry(country);
        
        // Prioritize email for US and BR (for testing)
        setShouldPrioritizeEmail(country === 'US' || country === 'BR');
        
        console.log('📱 Phone country detected:', country, {
          prefix: formData.phonePrefix,
          phone: formData.phone,
          prioritizeEmail: country === 'US' || country === 'BR'
        });
      } catch (error) {
        console.error('Error parsing form data:', error);
      }
    }
  }, []);

  // TEMPORARY: SMS sending is disabled - only email verification is available
  // TODO: Re-enable SMS sending in the future
  useEffect(() => {
    // Don't send SMS automatically - email verification is the only method available
    console.log('📧 Email verification only - SMS disabled');
    return;

    // DISABLED CODE - Keep for future re-enablement
    /*
    if (shouldPrioritizeEmail) {
      console.log('📧 Email prioritized - skipping automatic SMS send');
      return;
    }

    const sendSMS = async () => {
      const formDataStr = sessionStorage.getItem('bookingFormData');
      if (!formDataStr) {
        toast.error('Booking data not found');
        navigate('/book/step-1');
        return;
      }

      const formData = JSON.parse(formDataStr);
      
      const fullPhone = `${formData.phonePrefix}${formData.phone}`;

      try {
        // Always use Supabase Phone OTP (native method)
        console.log('📱 Sending Supabase Phone OTP for booking');
        const result = await sendSupabaseOTP(fullPhone);

        if (!result.success) {
          toast.error(result.error || 'Failed to send SMS. Please try again.');
          console.error('Supabase OTP send error:', result.error);
        } else {
          if (result.devCode) {
            console.log('🔐 DEV MODE - Verification code:', result.devCode);
            setDevCode(result.devCode);
            toast.success(`DEV: Code is ${result.devCode}`, { duration: 10000 });
          } else {
            toast.success('Verification code sent!');
          }
        }
      } catch (error: any) {
        console.error('Error sending SMS:', error);
        toast.error('Failed to send SMS. Please try again.');
      }
    };

    if (!hasSentSMS.current) {
      hasSentSMS.current = true;
      sendSMS();
    }
    */
  }, [navigate, shouldPrioritizeEmail]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleCodeChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`code-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleSubmit = async () => {
    if (code.every(digit => digit !== '') && consent) {
      setIsLoading(true);
      
      // Get phone from form data
      const formDataStr = sessionStorage.getItem('bookingFormData');
      if (!formDataStr) {
        toast.error('Booking data not found');
        setIsLoading(false);
        navigate('/book/step-1');
        return;
      }
      
      const formData = JSON.parse(formDataStr);
      const enteredCode = code.join('');
      
      // Use Supabase Phone OTP (native method)
      // This automatically establishes JWT session upon successful verification
      try {
        const fullPhone = `${formData.phonePrefix}${formData.phone}`;
        
        console.log('🔍 Verifying Supabase Phone OTP for booking');
        const result = await verifySupabaseOTP(fullPhone, enteredCode);
        
        if (!result.success) {
          toast.error(result.error || 'Invalid verification code. Please try again.');
          setIsLoading(false);
          return;
        }
        
        // SMS verified and Supabase Auth session established successfully
        // JWT session is automatically created by verifySupabaseOTP
        // User is now authenticated and can proceed to payment
        toast.success('Phone verified successfully!');
        console.log('✅ User authenticated via Supabase OTP - JWT session established');
        console.log('✅ Proceeding to payment with authenticated session');
        setIsLoading(false);
        navigate('/book/payment');
      } catch (error: any) {
        console.error('Error verifying SMS:', error);
        toast.error(error.message || 'Verification failed. Please try again.');
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="sticky top-0 z-10 bg-white border-b border-border px-4 py-3 flex items-center gap-3">
        <Link to="/book/step-1">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <h1 className="text-lg font-semibold">
          Verify Account
        </h1>
      </header>

      <div className="px-4 py-6 pb-40 space-y-6">
        {/* Progress */}
        <StepProgress currentStep={2} stepLabel="Verification" />

        {/* Icon - Dynamic based on priority */}
        <div className="flex justify-center">
          <div className="h-24 w-24 rounded-full bg-primary/10 flex items-center justify-center">
            {/* TEMPORARY: Always show email icon - SMS is disabled */}
            <Mail className="h-12 w-12 text-primary" />
          </div>
        </div>

        {/* Heading - Dynamic based on priority */}
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold">
            Verify Your Account
          </h2>
          <p className="text-muted-foreground">
            Continue with your email and password to confirm your identity before proceeding with your booking.
          </p>
          {devCode && (
            <div className="mt-4 p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
              <p className="text-sm font-semibold text-yellow-800 mb-1">🔐 DEV MODE - Verification Code:</p>
              <p className="text-3xl font-bold text-yellow-900 tracking-wider">{devCode}</p>
              <p className="text-xs text-yellow-700 mt-2">This code appears only in development</p>
            </div>
          )}
        </div>

        {/* TEMPORARY: USA Notice removed - SMS is disabled for all countries, email is the only option */}
        {/* TODO: Re-enable USA notice when SMS is re-enabled for non-USA countries */}

        {/* TEMPORARY: Only Email verification is available - SMS is disabled */}
        {/* TODO: Re-enable SMS verification in the future */}
        {/* Conditional Layout: Email First (US/BR) or SMS First (CA/Other) */}
        {/* Show Email option for both cases (USA/BR and Others) */}
        <div className="max-w-md mx-auto">
          <Card className="p-6 border-2 border-primary/20 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Continue with Email</h3>
                <p className="text-sm text-muted-foreground">Quick and secure</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Verify your account using the email and password you provided.
            </p>
            <Button
              className="w-full h-12 text-base font-semibold rounded-xl"
              onClick={async () => {
                    setIsCreatingAccount(true);
                    
                    try {
                      const formDataStr = sessionStorage.getItem('bookingFormData');
                      if (!formDataStr) {
                        toast.error('Booking data not found');
                        navigate('/book/step-1');
                        return;
                      }
                      
                      const formData = JSON.parse(formDataStr);
                      
                      // Validate that email and password are present
                      if (!formData.email || !formData.password) {
                        toast.error('Email and password are required. Please go back and fill in all fields.');
                        setIsCreatingAccount(false);
                        return;
                      }
                      
                      const fullPhone = `${formData.phonePrefix}${formData.phone}`;
                      
                      // First, try to sign in (optimistic approach - most users will already have accounts)
                      console.log('🔐 Attempting to sign in with email:', formData.email);
                      let result = await signInWithEmailPassword(formData.email, formData.password);
                      
                      // If sign in fails, try to sign up (account doesn't exist yet)
                      if (!result.success) {
                        // Check if error is related to invalid credentials (account might not exist)
                        const isInvalidCredentials = result.error?.includes('Invalid login credentials') || 
                                                     result.error?.includes('invalid_credentials') ||
                                                     result.error?.includes('Email not confirmed');
                        
                        if (isInvalidCredentials) {
                          // Try to create account (might be new user or wrong password)
                          console.log('📝 Sign in failed, attempting to create account with email:', formData.email);
                          result = await signUpWithEmailPassword(
                            formData.email,
                            formData.password,
                            fullPhone,
                            formData.fullName
                          );
                          
                          // If sign up fails with "User already registered", the account exists but password might be wrong
                          if (!result.success && (result.error?.includes('already registered') || result.error?.includes('User already registered'))) {
                            // Account exists but login failed - password is likely wrong
                            toast.error('Invalid email or password. Please check your credentials.');
                            setIsCreatingAccount(false);
                            return;
                          } else if (!result.success) {
                            // Other sign up error
                            toast.error(result.error || 'Failed to create account. Please try again.');
                            setIsCreatingAccount(false);
                            return;
                          } else {
                            // Sign up successful
                            toast.success('Account created successfully!');
                            console.log('✅ User authenticated via email/password signup - JWT session established');
                            console.log('✅ Proceeding to payment with authenticated session');
                          }
                        } else {
                          // Other sign in error (not invalid credentials)
                          toast.error(result.error || 'Failed to sign in. Please try again.');
                          setIsCreatingAccount(false);
                          return;
                        }
                      } else {
                        // Sign in successful
                        toast.success('Signed in successfully!');
                        console.log('✅ User authenticated via email/password login - JWT session established');
                        console.log('✅ Proceeding to payment with authenticated session');
                      }
                      
                      // Remove password from sessionStorage for security
                      const { password, confirmPassword, ...formDataWithoutPassword } = formData;
                      sessionStorage.setItem('bookingFormData', JSON.stringify(formDataWithoutPassword));
                      
                      setIsCreatingAccount(false);
                      navigate('/book/payment');
                    } catch (error: any) {
                      console.error('Error with email authentication:', error);
                      toast.error(error.message || 'Failed to authenticate. Please try again.');
                      setIsCreatingAccount(false);
                    }
                  }}
                  disabled={isCreatingAccount || isLoading}
                >
                  {isCreatingAccount ? 'Authenticating...' : 'Continue with Email'}
                </Button>
              </Card>
            </div>

        {/* TEMPORARY: SMS verification is completely disabled - all SMS-related UI is hidden */}
        {/* TODO: Re-enable SMS verification in the future */}
        {/* All SMS-related code is commented out below for future re-enablement */}
        {false && (
          <>
            {/* SMS Option - Secondary - DISABLED */}
            <div className="flex flex-col items-center gap-4 pt-2">
              <div className="relative w-full max-w-md">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-muted-foreground/20"></span>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-[#F9FAFB] px-2 text-muted-foreground">Or use SMS</span>
                </div>
              </div>
              
              <div className="max-w-md mx-auto w-full">
                <Button
                  variant="outline"
                  className="w-full h-12 text-base font-semibold rounded-xl border-2"
                  onClick={async () => {
                    // Send SMS code when user clicks this option
                    const formDataStr = sessionStorage.getItem('bookingFormData');
                    if (!formDataStr) {
                      toast.error('Booking data not found');
                      navigate('/book/step-1');
                      return;
                    }
                    
                    const formData = JSON.parse(formDataStr);
                    const fullPhone = `${formData.phonePrefix}${formData.phone}`;
                    
                    try {
                      console.log('📱 Sending Supabase Phone OTP for booking');
                      const result = await sendSupabaseOTP(fullPhone);
                      
                      if (!result.success) {
                        toast.error(result.error || 'Failed to send SMS. Please try again.');
                        console.error('Supabase OTP send error:', result.error);
                      } else {
                        // Mark SMS as sent to show input fields
                        setSmsSent(true);
                        
                        if (result.devCode) {
                          console.log('🔐 DEV MODE - Verification code:', result.devCode);
                          setDevCode(result.devCode);
                          toast.success(`DEV: Code is ${result.devCode}`, { duration: 10000 });
                        } else {
                          toast.success('Verification code sent!');
                        }
                        setResendTimer(30);
                      }
                    } catch (error: any) {
                      console.error('Error sending SMS:', error);
                      toast.error('Failed to send SMS. Please try again.');
                    }
                  }}
                  disabled={isCreatingAccount || isLoading || smsSent}
                >
                  <Phone className="h-4 w-4 mr-2" />
                  {smsSent ? 'SMS Code Sent' : 'Verify with SMS'}
                </Button>
              </div>
            </div>

            {/* SMS Verification Code Input - Show if SMS was sent (devCode or smsSent) */}
            {(devCode || smsSent) && (
              <>
        <div className="flex flex-col items-center">
          <div className="space-y-4 flex flex-col">
                    <Label className="text-[#565B66]">SMS Verification Code</Label>
            <div className="flex gap-2">
              {code.map((digit, index) => (
                <Input
                  key={index}
                  id={`code-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  className="w-12 h-14 text-center text-2xl font-semibold"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Resend Timer */}
        <div className="text-center">
        {resendTimer > 0 ? (
            <p className="text-sm text-muted-foreground">Resend Code in {resendTimer}s...</p>
          ) : (
            <button
              className="text-sm text-primary font-medium"
              onClick={async () => {
                const formDataStr = sessionStorage.getItem('bookingFormData');
                if (!formDataStr) return;
                
                const formData = JSON.parse(formDataStr);
                setResendTimer(30);
                
                // Always use production mode
                try {
                    const fullPhone = `${formData.phonePrefix}${formData.phone}`;
                    console.log('📱 Resending Supabase Phone OTP');
                    const result = await sendSupabaseOTP(fullPhone);
                    
                    if (!result.success) {
                      toast.error(result.error || 'Failed to resend code');
                    } else {
                      if (result.devCode) {
                        console.log('🔐 DEV MODE - Verification code:', result.devCode);
                        setDevCode(result.devCode);
                        toast.success(`DEV: New code is ${result.devCode}`, { duration: 10000 });
                      } else {
                        toast.success('New code sent!');
                      }
                    }
                  } catch (error) {
                    toast.error('Failed to resend code');
                  }
              }}
            >
              Resend Code
            </button>
          )}
        </div>

        {/* Consent */}
        <div className="flex items-start gap-3 justify-center max-w-md mx-auto">
          <Checkbox 
            id="consent" 
            checked={consent}
            onCheckedChange={(checked) => setConsent(checked === true)}
            className="mt-0.5"
          />
          <Label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer text-foreground/80 font-normal">
            I agree to receive SMS messages for booking verification and service updates. Standard message rates may apply.
          </Label>
        </div>
              </>
            )}

            {/* SMS Priority Layout (CA/Other) - DISABLED */}
            <>
              {/* Verification Code Input - DISABLED */}
              <div className="flex flex-col items-center">
                <div className="space-y-4 flex flex-col">
                  <Label className="text-[#565B66]">Verification Code</Label>
                  <div className="flex gap-2">
                    {code.map((digit, index) => (
                      <Input
                        key={index}
                        id={`code-${index}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        className="w-12 h-14 text-center text-2xl font-semibold"
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Resend Timer - DISABLED */}
              <div className="text-center">
                {resendTimer > 0 ? (
                  <p className="text-sm text-muted-foreground">Resend Code in {resendTimer}s...</p>
                ) : (
                  <button
                    className="text-sm text-primary font-medium"
                    onClick={async () => {
                      const formDataStr = sessionStorage.getItem('bookingFormData');
                      if (!formDataStr) return;
                      
                      const formData = JSON.parse(formDataStr);
                      setResendTimer(30);
                      
                      if (true) { // Always use production mode
                        try {
                          const fullPhone = `${formData.phonePrefix}${formData.phone}`;
                          console.log('📱 Resending Supabase Phone OTP');
                          const result = await sendSupabaseOTP(fullPhone);
                          
                          if (!result.success) {
                            toast.error(result.error || 'Failed to resend code');
                          } else {
                            if (result.devCode) {
                              console.log('🔐 DEV MODE - Verification code:', result.devCode);
                              setDevCode(result.devCode);
                              toast.success(`DEV: New code is ${result.devCode}`, { duration: 10000 });
                            } else {
                              toast.success('New code sent!');
                            }
                          }
                        } catch (error) {
                          toast.error('Failed to resend code');
                        }
                      }
                    }}
                  >
                    Resend Code
                  </button>
                )}
              </div>

              {/* Consent - DISABLED */}
              <div className="flex items-start gap-3 justify-center max-w-md mx-auto">
                <Checkbox 
                  id="consent" 
                  checked={consent}
                  onCheckedChange={(checked) => setConsent(checked === true)}
                  className="mt-0.5"
                />
                <Label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer text-foreground/80 font-normal">
                  I agree to receive SMS messages for booking verification and service updates. Standard message rates may apply.
                </Label>
              </div>

              {/* Use Email Option - Secondary - DISABLED */}
              <div className="flex flex-col items-center gap-4 pt-2 pb-4">
                <div className="relative w-full max-w-md">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-muted-foreground/20"></span>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-[#F9FAFB] px-2 text-muted-foreground">Or</span>
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  className="w-full max-w-md h-12 text-base font-semibold rounded-xl border-2"
                  onClick={async () => {
                    setIsCreatingAccount(true);
                    
                    try {
                      const formDataStr = sessionStorage.getItem('bookingFormData');
                      if (!formDataStr) {
                        toast.error('Booking data not found');
                        navigate('/book/step-1');
                        return;
                      }
                      
                      const formData = JSON.parse(formDataStr);
                      
                      // Validate that email and password are present
                      if (!formData.email || !formData.password) {
                        toast.error('Email and password are required. Please go back and fill in all fields.');
                        setIsCreatingAccount(false);
                        return;
                      }
                      
                      const fullPhone = `${formData.phonePrefix}${formData.phone}`;
                      
                      // First, try to sign in (optimistic approach - most users will already have accounts)
                      console.log('🔐 Attempting to sign in with email:', formData.email);
                      let result = await signInWithEmailPassword(formData.email, formData.password);
                      
                      // If sign in fails, try to sign up (account doesn't exist yet)
                      if (!result.success) {
                        // Check if error is related to invalid credentials (account might not exist)
                        const isInvalidCredentials = result.error?.includes('Invalid login credentials') || 
                                                     result.error?.includes('invalid_credentials') ||
                                                     result.error?.includes('Email not confirmed');
                        
                        if (isInvalidCredentials) {
                          // Try to create account (might be new user or wrong password)
                          console.log('📝 Sign in failed, attempting to create account with email:', formData.email);
                          result = await signUpWithEmailPassword(
                            formData.email,
                            formData.password,
                            fullPhone,
                            formData.fullName
                          );
                          
                          // If sign up fails with "User already registered", the account exists but password might be wrong
                          if (!result.success && (result.error?.includes('already registered') || result.error?.includes('User already registered'))) {
                            // Account exists but login failed - password is likely wrong
                            toast.error('Invalid email or password. Please check your credentials.');
                            setIsCreatingAccount(false);
                            return;
                          } else if (!result.success) {
                            // Other sign up error
                            toast.error(result.error || 'Failed to create account. Please try again.');
                            setIsCreatingAccount(false);
                            return;
                          } else {
                            // Sign up successful
                            toast.success('Account created successfully!');
                            console.log('✅ User authenticated via email/password signup - JWT session established');
                            console.log('✅ Proceeding to payment with authenticated session');
                          }
                        } else {
                          // Other sign in error (not invalid credentials)
                          toast.error(result.error || 'Failed to sign in. Please try again.');
                          setIsCreatingAccount(false);
                          return;
                        }
                      } else {
                        // Sign in successful
                        toast.success('Signed in successfully!');
                        console.log('✅ User authenticated via email/password login - JWT session established');
                        console.log('✅ Proceeding to payment with authenticated session');
                      }
                      
                      // Remove password from sessionStorage for security
                      const { password, confirmPassword, ...formDataWithoutPassword } = formData;
                      sessionStorage.setItem('bookingFormData', JSON.stringify(formDataWithoutPassword));
                      
                      setIsCreatingAccount(false);
                      navigate('/book/payment');
                    } catch (error: any) {
                      console.error('Error with email authentication:', error);
                      toast.error(error.message || 'Failed to authenticate. Please try again.');
                      setIsCreatingAccount(false);
                    }
                  }}
                  disabled={isCreatingAccount || isLoading}
                >
                  {isCreatingAccount ? 'Authenticating...' : 'Use Email Instead'}
                </Button>
              </div>
            </>
          </>
        )}
      </div>

      {/* TEMPORARY: Fixed Bottom CTA - Only Email button is shown */}
      {/* TODO: Re-enable SMS verification button in the future */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border p-4">
        <Button 
          className="w-full h-14 text-base font-semibold rounded-xl"
          onClick={async () => {
            setIsCreatingAccount(true);
            
            try {
              const formDataStr = sessionStorage.getItem('bookingFormData');
              if (!formDataStr) {
                toast.error('Booking data not found');
                navigate('/book/step-1');
                return;
              }
              
              const formData = JSON.parse(formDataStr);
              
              // Validate that email and password are present
              if (!formData.email || !formData.password) {
                toast.error('Email and password are required. Please go back and fill in all fields.');
                setIsCreatingAccount(false);
                return;
              }
              
              const fullPhone = `${formData.phonePrefix}${formData.phone}`;
              
              // First, try to sign in (optimistic approach - most users will already have accounts)
              console.log('🔐 Attempting to sign in with email:', formData.email);
              let result = await signInWithEmailPassword(formData.email, formData.password);
              
              // If sign in fails, try to sign up (account doesn't exist yet)
              if (!result.success) {
                // Check if error is related to invalid credentials (account might not exist)
                const isInvalidCredentials = result.error?.includes('Invalid login credentials') || 
                                             result.error?.includes('invalid_credentials') ||
                                             result.error?.includes('Email not confirmed');
                
                if (isInvalidCredentials) {
                  // Try to create account (might be new user or wrong password)
                  console.log('📝 Sign in failed, attempting to create account with email:', formData.email);
                  result = await signUpWithEmailPassword(
                    formData.email,
                    formData.password,
                    fullPhone,
                    formData.fullName
                  );
                  
                  // If sign up fails with "User already registered", the account exists but password might be wrong
                  if (!result.success && (result.error?.includes('already registered') || result.error?.includes('User already registered'))) {
                    // Account exists but login failed - password is likely wrong
                    toast.error('Invalid email or password. Please check your credentials.');
                    setIsCreatingAccount(false);
                    return;
                  } else if (!result.success) {
                    // Other sign up error
                    toast.error(result.error || 'Failed to create account. Please try again.');
                    setIsCreatingAccount(false);
                    return;
                  } else {
                    // Sign up successful
                    toast.success('Account created successfully!');
                    console.log('✅ User authenticated via email/password signup - JWT session established');
                    console.log('✅ Proceeding to payment with authenticated session');
                  }
                } else {
                  // Other sign in error (not invalid credentials)
                  toast.error(result.error || 'Failed to sign in. Please try again.');
                  setIsCreatingAccount(false);
                  return;
                }
              } else {
                // Sign in successful
                toast.success('Signed in successfully!');
                console.log('✅ User authenticated via email/password login - JWT session established');
                console.log('✅ Proceeding to payment with authenticated session');
              }
              
              // Remove password from sessionStorage for security
              const { password, confirmPassword, ...formDataWithoutPassword } = formData;
              sessionStorage.setItem('bookingFormData', JSON.stringify(formDataWithoutPassword));
              
              setIsCreatingAccount(false);
              navigate('/book/payment');
            } catch (error: any) {
              console.error('Error with email authentication:', error);
              toast.error(error.message || 'Failed to authenticate. Please try again.');
              setIsCreatingAccount(false);
            }
          }}
          disabled={isCreatingAccount || isLoading}
        >
          {isCreatingAccount ? 'Authenticating...' : 'Continue with Email'}
        </Button>
      </div>
    </div>
  );
}
